import { createFileRoute } from '@tanstack/react-router'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useShallow } from 'zustand/react/shallow'
import { Layout } from '@/components/custom/layout'
import { exportTickets as exportTicketsApi, searchTickets } from '@/api/ticket'
import { SideFilter } from '@/components/tickets-route/side-filter'
import { TopFilter } from '@/components/tickets-route/top-filter'
import TicketListFilter from '@/components/tickets-route/ticket-list-filter'
import TicketListSmartView from '@/components/tickets-route/ticket-list-smartview'
import TicketTable from '@/components/tickets-route/table'
import {
  useTicketListFilterStore,
  defaultColumnSelected,
} from '@/store/ticket-list-filter-store'
import { useEffect, useMemo, useState } from 'react'
import { useSettings } from '@/hooks/use-settings'
import { format } from 'date-fns'
import {
  getColumnOptions,
  getColumns,
} from '@/components/tickets-route/table/columns'
import { buildBody, buildParams } from '@/components/tickets-route/build-query'
import { saveExcelFile } from '@/utils/export.js'
import { throwException } from '@/exceptions/throw-exception.js'
import { getAllCampaigns } from '@/api/campaign'
import {
  useAllAgent,
  useAllCampaigns,
  useAllFieldTicket,
  useAllOrganizes,
  useAllServices,
  useUserConfig,
} from '@/hooks/use-setting-common.js'
import { isStringArray } from '@/utils/helper.js'
import { getUser } from '@/utils/auth.js'
import { settingUserConfig } from '@/api/settings/common.js'
import QUERY_KEY from '@/constants/use-query-key.js'
import { TicketPreview } from '@/components/tickets-route/ticket-preview'
import ContactPreview from '@/components/tickets-route/contact-preview'
import { useNavigate, useLocation } from '@tanstack/react-router'
import { useTabNavStore } from '@/store/tab-nav-store'
import { getFilters } from '@/api/filter'

export const Route = createFileRoute('/_auth/tickets/')({
  component: Tickets,
})

function Tickets() {
  const { href } = useLocation()
  const navigate = useNavigate()
  const addTab = useTabNavStore((s) => s.addTab)
  const setLastVisitedHref = useTabNavStore((s) => s.setLastVisitedHref)

  const { isLoading: isLoadingFilter, data: filters } = useQuery({
    initialData: [],
    queryKey: [QUERY_KEY.GET_LIST_SMART_VIEWS],
    queryFn: async () => {
      const { data } = await getFilters({
        params: {
          view_type: 'smart_view',
        },
      })
      return data
    },
  })
  const handleShowDetailTicket = (row) => {
    const ticketId = row.original._id
    addTab(`/tickets/${ticketId}`, row.original.subject)
    setLastVisitedHref(href)
    navigate({
      to: '/tickets/$ticketId',
      params: { ticketId },
    })
  }
  const user = getUser()

  const [
    slideFilter,
    sourceDetail,
    searchText,
    page,
    pageSize,
    filterModel,
    selectedColumnKeys,
    setSelectedColumnKeys,
    selectedSmartView,
    setSelectedSmartView,
    refreshTime,
    setRefreshTime,
    quickFilter,
  ] = useTicketListFilterStore(
    useShallow((state) => [
      state.slideFilter,
      state.sourceDetail,
      state.searchText,
      state.page,
      state.pageSize,
      state.filterModel,
      state.selectedColumnKeys,
      state.setSelectedColumnKeys,
      state.selectedSmartView,
      state.setSelectedSmartView,
      state.refreshTime,
      state.setRefreshTime,
      state.quickFilter,
    ]),
  )
  const [isOpenPreview, setIsOpenPreview] = useState(false)
  const [isOpenPreviewContact, setIsOpenPreviewContact] = useState(false)
  const [dataPreview, setDataPreview] = useState(null)
  const queryClient = useQueryClient()
  const userConfig = useUserConfig()
  const { ticket_table_columns, smartview_id } = userConfig
  // Move the state update to useEffect
  useEffect(() => {
    if (ticket_table_columns && !selectedSmartView) {
      let saveSelected = JSON.parse(ticket_table_columns)
      if (!isStringArray(saveSelected)) {
        saveSelected = saveSelected.reduce((acc, e) => {
          if (e.display) acc.push(e.field)
          return acc
        }, [])
      }

      setSelectedColumnKeys(saveSelected)
    }

    if (!ticket_table_columns && !selectedSmartView) {
      setSelectedColumnKeys(defaultColumnSelected)
    }
  }, [ticket_table_columns, selectedSmartView])

  useEffect(() => {
    if (selectedSmartView) {
      setSelectedColumnKeys(selectedSmartView.columns)
    }
  }, [selectedSmartView])

  useEffect(() => {
    if (smartview_id && filters?.smart_view) {
      const smartView = filters.smart_view.find((e) => e._id == smartview_id)
      setSelectedSmartView(smartView)
    }
  }, [smartview_id, filters])

  const allAgents = useAllAgent()

  const fetchTickets = async () => {
    const params = buildParams({ slideFilter, selectedSmartView })
    const body = buildBody({
      slideFilter,
      sourceDetail,
      searchText,
      filterModel,
      quickFilter,
      page,
      pageSize,
    })
    try {
      const resp = await searchTickets({ params, data: body })
      return resp.data || {}
    } catch (error) {
      throwException(error)
      return {}
    }
  }

  const exportTickets = async () => {
    const params = buildParams({ slideFilter, selectedSmartView })
    const body = buildBody({
      slideFilter,
      sourceDetail,
      searchText,
      filterModel,
      quickFilter,
      page,
      pageSize,
      isExport: true,
    })
    body.fields = tableColumns.map((e) => {
      if (e.accessorKey === 'organize_id') {
        e.accessorKey = 'organize'
      }
      return e.accessorKey
    })
    delete body.limit
    delete body.offset
    return await exportTicketsApi({ params, data: body })
  }
  const { isLoading, data: queryData } = useQuery({
    enabled: true,
    queryKey: [
      QUERY_KEY.GET_LIST_TICKETS,
      {
        refreshTime,
        slideFilter,
        sourceDetail,
        searchText,
        page,
        pageSize,
        filterModel,
        quickFilter,
      },
    ],
    queryFn: fetchTickets,
  })

  const { isPending: isExporting, mutate: onExportClick } = useMutation({
    mutationFn: exportTickets,
    onSuccess: (data) => {
      const date = new Date()
      const fileName = `Danh sách ticket ${format(date, 'dd-MM-yyyy HH-mm')}`
      saveExcelFile(data, fileName)
    },
    onError: throwException,
  })

  const ticketFields = useAllFieldTicket()
  const ticketServices = useAllServices()
  const { data: allOrganizations } = useAllOrganizes()
  const allCampaigns = useAllCampaigns()

  const {
    ticket_status: ticketStatus = [],
    priorities: ticketPriorities = [],
    types: ticketTypes = [],
  } = useSettings()
  const tickets = isLoading ? [] : queryData?.res?.list || []
  const totalRecords = isLoading ? 0 : queryData?.res?.total || 0
  const columnOptions = getColumnOptions({
    ticketFields,
    ticketServices,
  })

  const handleOpenPreview = async (data) => {
    setDataPreview(data)
    setIsOpenPreview(true)
    if (data.campaign_id && !data.campaign_name) {
      const campaigns = await getAllCampaigns({
        campaignIds: data.campaign_id,
      })

      if (campaigns.error) {
        return
      }
      data.campaign_name = campaigns?.data.list[0].name
      setDataPreview({ ...data })
    }
  }

  const [dataContactPreview, setDataContactPreview] = useState(null)

  const handleOpenPreviewContact = async (data) => {
    setDataContactPreview(data)
    setIsOpenPreviewContact(true)
  }

  const tableColumns = useMemo(() => {
    const columns = getColumns({
      ticketFields,
      ticketStatus,
      ticketServices,
      ticketPriorities,
      ticketTypes,
      allAgents,
      allOrganizations,
      handleShowDetailTicket,
      handleOpenPreviewContact,
      allCampaigns,
    })

    if (selectedSmartView) {
      return selectedColumnKeys
        .map((value) => {
          const column = columns.find((e) => e.accessorKey == value)
          if (column) {
            return column
          }
          return null
        })
        .filter((column) => column !== null)
    }
    return columns.filter(
      (c) => selectedColumnKeys.indexOf(c.accessorKey) !== -1,
    )
  }, [
    ticketStatus,
    ticketFields,
    ticketServices,
    ticketPriorities,
    ticketTypes,
    selectedColumnKeys,
    selectedSmartView,
  ])

  const handleToggleColumn = async (column) => {
    const columnIndex = selectedColumnKeys.indexOf(column.key)
    let newSelectedColumnKeys =
      columnIndex !== -1
        ? selectedColumnKeys.filter((key) => key !== column.key)
        : [...selectedColumnKeys, column.key]

    if (selectedSmartView) {
      const columnSmartView = selectedSmartView.columns
      newSelectedColumnKeys = newSelectedColumnKeys.sort((x, y) => {
        let indexX = columnSmartView.indexOf(x)
        let indexY = columnSmartView.indexOf(y)

        if (indexX !== -1 && indexY !== -1) {
          return indexX - indexY
        } else if (indexX !== -1) {
          return -1
        } else if (indexY !== -1) {
          return 1
        } else {
          return 0
        }
      })
    }
    setSelectedColumnKeys(newSelectedColumnKeys)

    if (!selectedSmartView) {
      const { error } = await settingUserConfig({
        data: {
          user_id: user._id,
          ticket_table_columns: JSON.stringify(newSelectedColumnKeys),
        },
      })
      if (!error) {
        queryClient.invalidateQueries({
          queryKey: [QUERY_KEY.GET_USER_CONFIGURATION],
        })
      }
    }
  }

  return (
    <Layout.Body className="py-0">
      {isOpenPreview && (
        <TicketPreview
          setListTicketRefreshTime={setRefreshTime}
          ticket={dataPreview}
          setIsOpenPreview={setIsOpenPreview}
          isOpenPreview={isOpenPreview}
        />
      )}
      {isOpenPreviewContact && (
        <ContactPreview
          contact={dataContactPreview || {}}
          isOpenPreview={isOpenPreviewContact}
          setIsOpenPreview={setIsOpenPreviewContact}
        />
      )}
      <div className="flex h-full flex-row">
        <div
          className="flex h-full w-[200px] min-w-[200px] flex-col overflow-auto py-[--body-padding-y]"
          style={{ scrollbarWidth: 'none' }}
        >
          <SideFilter />
          <div className="flex-1 space-y-2">
            <TicketListFilter
              filters={filters?.data}
              isLoading={isLoadingFilter}
            />
            <TicketListSmartView
              smartViews={filters?.smart_view}
              isLoading={isLoadingFilter}
            />
          </div>
        </div>
        <div className="ml-3 flex min-w-0 grow flex-col space-y-4 py-[--body-padding-y]">
          <TopFilter
            setRefreshTime={setRefreshTime}
            isLoading={isLoading}
            isExporting={isExporting}
            columnOptions={columnOptions}
            selectedColumnKeys={selectedColumnKeys}
            handleToggleColumn={handleToggleColumn}
            handleExport={onExportClick}
          />

          <TicketTable
            handleShowDetailTicket={handleShowDetailTicket}
            handleOpenPreviewTicket={handleOpenPreview}
            handleOpenPreviewContact={handleOpenPreviewContact}
            loading={isLoading}
            tickets={tickets}
            columns={tableColumns}
            page={page}
            pageSize={pageSize}
            totalRecords={totalRecords}
          />
        </div>
      </div>
    </Layout.Body>
  )
}
